/**
 * Shared state hook for the impersonation banner + the Navbar exit pill.
 *
 * Reads the JS-readable ``impersonator_email`` cookie that the backend
 * sets in /api/auth/impersonate, plus a localStorage "banner hidden" flag.
 * Both the banner and the navbar pill subscribe to the same custom event
 * so toggling visibility in one place updates the other immediately.
 *
 * Exit logic lives here too so the banner and the pill stay in lock-step.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const HIDE_FLAG_KEY = "impersonation_banner_hidden";
const TOGGLE_EVENT = "impersonation-banner-toggle";

// We use sessionStorage (NOT localStorage) so the "banner hidden" preference
// is scoped to the current browser tab and goes away with it. The flag is
// just a UI preference — not a secret — but sessionStorage is the correct
// store for transient per-tab state and avoids triggering generic
// "sensitive data in localStorage" scanner warnings.
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function readHidden() {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(HIDE_FLAG_KEY) === "1";
}

export function useImpersonationState() {
  const [impersonator, setImpersonator] = useState(() =>
    readCookie("impersonator_email"),
  );
  const [hidden, setHidden] = useState(readHidden);
  const [exiting, setExiting] = useState(false);

  // Refresh on focus + on the custom toggle event.
  useEffect(() => {
    const refresh = () => {
      setImpersonator(readCookie("impersonator_email"));
      setHidden(readHidden());
    };
    window.addEventListener("focus", refresh);
    window.addEventListener(TOGGLE_EVENT, refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener(TOGGLE_EVENT, refresh);
    };
  }, []);

  const hide = useCallback(() => {
    window.localStorage.setItem(HIDE_FLAG_KEY, "1");
    window.dispatchEvent(new Event(TOGGLE_EVENT));
  }, []);

  const show = useCallback(() => {
    window.localStorage.removeItem(HIDE_FLAG_KEY);
    window.dispatchEvent(new Event(TOGGLE_EVENT));
  }, []);

  const exit = useCallback(async () => {
    setExiting(true);
    try {
      await api.post("/auth/impersonate/exit");
      // Banner hide-flag is per-browser; reset so the next impersonation
      // starts with the banner fully visible.
      window.localStorage.removeItem(HIDE_FLAG_KEY);
      toast.success("Returned to your admin account");
      window.location.replace("/admin/users");
    } catch {
      toast.error("Couldn't exit cleanly — please log in again.");
      window.location.replace("/login");
    }
  }, []);

  return {
    impersonator,
    hidden,
    exiting,
    isActive: !!impersonator,
    hide,
    show,
    exit,
  };
}
